/**
 * Client-side analytics capture + pre-consent event queue.
 *
 * `captureEvent` is the single entry point for every client-side analytics
 * event — never call `posthog.capture` directly. When PostHog is opted out
 * (before cookie consent) events are saved to localStorage; once the user
 * opts in, `replayQueuedEvents` flushes them.
 *
 * This closes the consent gap: events fired before a user accepts the cookie
 * banner would otherwise be silently dropped.
 */

import posthog from "posthog-js";
import { getEnvironment } from "@/config/environment";

const QUEUE_KEY = "flowly-analytics-queue";
const ENABLED = getEnvironment() === "production";

interface QueuedEvent {
  event: string;
  properties: Record<string, unknown>;
  timestamp: string;
}

function readQueue(): QueuedEvent[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as QueuedEvent[];
  } catch {
    return [];
  }
}

function writeQueue(queue: QueuedEvent[]): void {
  if (queue.length === 0) {
    localStorage.removeItem(QUEUE_KEY);
  } else {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  }
}

/**
 * Capture a PostHog event, or queue it if PostHog is currently opted out.
 * The single entry point for all client-side analytics — always use this
 * instead of calling `posthog.capture` directly.
 */
export function captureEvent(
  event: string,
  properties?: Record<string, unknown>
): void {
  // Non-production builds never load PostHog (see src/main.tsx) — short-circuit
  // here too so we don't accumulate dev events in localStorage that will never
  // be flushed.
  if (!ENABLED) return;

  if (posthog.has_opted_in_capturing()) {
    posthog.capture(event, properties);
    return;
  }

  const queue = readQueue();
  queue.push({
    event,
    properties: properties ?? {},
    timestamp: new Date().toISOString(),
  });
  writeQueue(queue);
}

/**
 * Identify the current user to PostHog. No-op in non-production (PostHog is
 * never initialized there) so we never ship PII to an uninitialized stub.
 */
export function identifyUser(
  distinctId: string,
  properties?: Record<string, unknown>
): void {
  if (!ENABLED) return;
  posthog.identify(distinctId, properties);
}

/** Reset PostHog identity on sign-out. No-op in non-production. */
export function resetAnalytics(): void {
  if (!ENABLED) return;
  posthog.reset();
}

/** Opt in to capturing after consent. No-op in non-production. */
export function optInCapturing(): void {
  if (!ENABLED) return;
  posthog.opt_in_capturing();
}

/** Opt out of capturing. No-op in non-production. */
export function optOutCapturing(): void {
  if (!ENABLED) return;
  posthog.opt_out_capturing();
}

/**
 * Replay all queued events to PostHog and clear the queue.
 * Call this after the user opts in to analytics.
 */
export function replayQueuedEvents(): void {
  if (!ENABLED) {
    writeQueue([]);
    return;
  }

  const queue = readQueue();
  if (queue.length === 0) return;

  for (const { event, properties, timestamp } of queue) {
    posthog.capture(event, { ...properties, $timestamp: timestamp });
  }

  writeQueue([]);
}
